import { useCallback, useEffect, useState } from "react";
import { decode, encode, type ViewState } from "./viewState";

// Keeps ViewState in sync with the URL. Reads the initial state from the current
// location (so a copied/bookmarked link restores the view), listens to popstate
// (so browser Back/Forward restore prior views), and pushes a new history entry
// on each patch. Reload re-reads the URL on mount.
export function useViewState(): [ViewState, (patch: Partial<ViewState>) => void] {
  const [state, setState] = useState<ViewState>(() => decode(window.location.search));

  useEffect(() => {
    const onPop = () => setState(decode(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const patch = useCallback((p: Partial<ViewState>) => {
    setState((prev) => {
      const next = { ...prev, ...p };
      const qs = encode(next);
      window.history.pushState(null, "", qs || window.location.pathname);
      return next;
    });
  }, []);

  return [state, patch];
}
